import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Receipt, Plus } from 'lucide-react';import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';import { Badge } from '@/app/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { useMyExpenses, useSubmitExpense } from '@/hooks/use-payroll';
import { useOrgCurrency } from '@/hooks/use-settings';
import { formatMoney } from '@/lib/currency';
import type { ExpenseStatus } from '@/lib/types';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  submitted: 'bg-blue-100 text-blue-800',
  under_review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  reimbursed: 'bg-purple-100 text-purple-800',
};

export function MyExpensesPage() {
  const orgCurrency = useOrgCurrency();
  const [statusFilter, setStatusFilter] = useState<ExpenseStatus | ''>('');

  const { data: expenses, isLoading } = useMyExpenses({ status: statusFilter || undefined });
  const submitExpense = useSubmitExpense();

  const unreimbursed = expenses?.filter(
    (e) => e.status !== 'reimbursed' && e.status !== 'rejected' && e.status !== 'draft'
  );
  const unreimbursedTotal = unreimbursed?.reduce(
    (sum, e) => sum + parseFloat(e.amount),
    0
  ) ?? 0;

  const draftCount = expenses?.filter((e) => e.status === 'draft').length ?? 0;
  const pendingCount = expenses?.filter((e) => e.status === 'submitted' || e.status === 'under_review').length ?? 0;
  const approvedCount = expenses?.filter((e) => e.status === 'approved').length ?? 0;

  const handleQuickSubmit = (id: number) => {
    submitExpense.mutate(id);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Expenses</h1>
          <p className="text-gray-600 mt-1">Track your expense submissions and reimbursements</p>
        </div>
        <Button asChild>
          <Link to="/payroll/expenses/new">
            <Plus className="h-4 w-4 mr-2" />
            New Expense
          </Link>
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Unreimbursed</p>
            <p className="text-2xl font-bold text-blue-600">
              {formatMoney(unreimbursedTotal, orgCurrency)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Drafts</p>
            <p className="text-2xl font-bold">{draftCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Approved</p>
            <p className="text-2xl font-bold text-green-600">{approvedCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v as ExpenseStatus | '')}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="reimbursed">Reimbursed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Expense List */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : !expenses?.length ? (
            <div className="p-8 text-center text-gray-500">
              <Receipt className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No expenses found</p>
              <Button asChild className="mt-4" variant="outline">
                <Link to="/payroll/expenses/new">Submit your first expense</Link>
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {expenses.map((expense) => (
                <div
                  key={expense.id}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <Link
                    to={`/payroll/expenses/${expense.id}`}
                    className="flex-1 min-w-0"
                  >
                    <p className="font-medium text-gray-900 truncate">{expense.description}</p>
                    <p className="text-sm text-gray-500">
                      {expense.date} &middot; {expense.category_name}
                      {expense.vendor && ` · ${expense.vendor}`}
                    </p>
                  </Link>
                  <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                    <span className="font-semibold">
                      {formatMoney(expense.amount, expense.currency ?? orgCurrency)}
                    </span>
                    <Badge className={statusColors[expense.status] ?? 'bg-gray-100 text-gray-800'}>
                      {expense.status.replace('_', ' ')}
                    </Badge>
                    {expense.status === 'draft' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleQuickSubmit(expense.id)}
                        disabled={submitExpense.isPending}
                      >
                        Submit
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
